import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { DatePickerCalendar } from "@/components/DatePickerCalendar";
import { AvatarBorder } from "@/components/AvatarBorder";
import { UserName } from "@/components/UserName";
import { Panel } from "../-components/Panel";
import { Countdown } from "@/components/ui/countdown";
import { ShinyText } from "@/components/ShinyText";
import { DefaultButton } from "@/components/DefaultButton";
import AnimatedLoading from "@/components/AnimatedLoading";
import confetti from "canvas-confetti";
import { useLuckyDrawDetailByDate, useStats } from "@/lib/hooks/useApi";
import type { LuckyDrawDetailByDateResponse } from "@/lib/types/api";
import { cn, generateTextShadow } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { t } from "@/lib/i18n";
import { UserAvatarIcon } from "@/components/LuckyDrawButton";

dayjs.extend(utc);
dayjs.extend(advancedFormat);

// ─── Date helpers (same pattern as rush leaderboard) ────────────────────

const getUtcEpochFromDate = (date: Date) => {
  const d = dayjs(date);
  return Math.floor(Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0) / 1000);
};

const getDateFromEpoch = (epoch?: number) => {
  if (!epoch || !Number.isFinite(epoch)) return undefined;
  const utcDate = dayjs.unix(epoch).utc();
  return new Date(Date.UTC(utcDate.year(), utcDate.month(), utcDate.date()));
};

// ─── Route ──────────────────────────────────────────────────────────────

type LuckyDrawHistorySearch = {
  date?: number;
};

export const Route = createFileRoute("/(game)/lucky-draw/history/")({
  validateSearch: (search): LuckyDrawHistorySearch => {
    const numericDate = Number(
      typeof search.date === "string" || typeof search.date === "number"
        ? search.date
        : undefined,
    );
    const result: LuckyDrawHistorySearch = {};
    if (Number.isFinite(numericDate) && numericDate > 0) {
      result.date = Math.floor(numericDate);
    }
    return result;
  },
  component: LuckyDrawHistoryRoute,
});

// ─── Component ──────────────────────────────────────────────────────────

function LuckyDrawHistoryRoute() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address ?? "";
  const { data: stats } = useStats(walletAddress);

  const setSearchDate = useCallback(
    (date: Date) => {
      const epoch = getUtcEpochFromDate(date);
      navigate({
        from: Route.fullPath,
        replace: true,
        search: (prev) => ({ ...prev, date: epoch }),
      });
    },
    [navigate],
  );

  const selectedEpoch = useMemo(() => {
    if (search.date && Number.isFinite(search.date)) return search.date;
    return getUtcEpochFromDate(new Date());
  }, [search.date]);

  const detailQuery = useLuckyDrawDetailByDate({
    epoch: selectedEpoch,
    userId: stats?.userId,
  });

  const currentDraw = detailQuery.data ?? null;
  const luckyDraw = currentDraw?.luckyDraw;
  const isLive = luckyDraw?.status === "live";
  const isDistributed = luckyDraw?.status === "distributed";
  const isWaiting = luckyDraw?.status === "waiting";
  const hasNoDraw = !detailQuery.isLoading && (!luckyDraw || isWaiting);
  const todayEpoch = useMemo(() => getUtcEpochFromDate(new Date()), []);
  const isFutureDate = selectedEpoch > todayEpoch;
  const particiPantCount = luckyDraw?.participantsCount;

  const sortedPrizes = useMemo(() => {
    if (!currentDraw?.prizes) return [];
    return [...currentDraw.prizes].sort((a, b) => a.prizeOrder - b.prizeOrder);
  }, [currentDraw?.prizes]);

  const hasWinners = sortedPrizes.some((p) => p.winner != null);

  // Sort entries: winners first, then the rest
  const orderedEntries = useMemo(() => {
    if (!currentDraw?.ticketEntries) return [];
    const entries = currentDraw.ticketEntries;
    const winners = entries.filter((e) => e.isWinner);
    const rest = entries.filter((e) => !e.isWinner);
    return [...winners, ...rest];
  }, [currentDraw?.ticketEntries]);

  const entryOrderMap = useMemo(
    () =>
      new Map(
        (currentDraw?.ticketEntries ?? []).map((entry, i) => [entry.id, i + 1]),
      ),
    [currentDraw?.ticketEntries],
  );

  // Confetti burst inside the header when winners are displayed (repeats every 2s)
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!hasWinners || !confettiCanvasRef.current) return;
    const myConfetti = confetti.create(confettiCanvasRef.current, {
      resize: true,
    });
    const burst = () => {
      const end = Date.now() + 800;
      const frame = () => {
        const randomColors = [
          ["#ff6b6b", "#ff8787", "#ffa8a8"],
          ["#4ecdc4", "#63e6de", "#99f6e4"],
          ["#a78bfa", "#c4b5fd", "#ddd6fe"],
          ["#fb923c", "#fdba74", "#fed7aa"],
          ["#f2c969", "#fde047", "#fbbf24"],
          ["#60a5fa", "#93c5fd", "#bfdbfe"],
          ["#f472b6", "#f9a8d4", "#fbcfe8"],
        ];
        const pick =
          randomColors[Math.floor(Math.random() * randomColors.length)];
        myConfetti({
          particleCount: 4,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0 },
          colors: pick,
          gravity: 0.8,
        });
        myConfetti({
          particleCount: 4,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0 },
          colors: pick,
          gravity: 0.8,
        });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    };
    // Fire immediately, then repeat every 4s
    burst();
    const interval = setInterval(burst, 2000);
    return () => {
      clearInterval(interval);
      myConfetti.reset();
    };
  }, [hasWinners]);

  if (detailQuery.isLoading) {
    return <AnimatedLoading />;
  }

  return (
    <div className="relative flex flex-1 min-h-0 flex-col bg-[#1D2A52] [&_img]:![image-rendering:pixelated]">
      {/* ── Header ── */}
      <FloatingLeftButton
        onClick={() => navigate({ to: "/lucky-draw" })}
        className="absolute left-0 top-4 z-[60]"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />
      {hasNoDraw ? (
        /* ── Empty state (Option C: full section replace) ── */
        <div className="flex-1 flex flex-col items-center">
          {/* Date picker only */}
          <div className="w-full flex justify-end z-50">
            <div className="pe-2 pt-2">
              <DatePickerCalendar
                value={getDateFromEpoch(selectedEpoch) ?? new Date()}
                onChange={setSearchDate}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4">
            <div className="text-center space-y-1">
              <h6
                className="text-2xl text-white font-semibold text-center uppercase"
                style={{ textShadow: generateTextShadow(0.65) }}
              >
                {isFutureDate
                  ? t("luckyDraw.history.notReadyYet")
                  : t("luckyDraw.history.noDrawOnThisDay")}
              </h6>
              <p
                className="text-white/80 text-sm text-center"
                style={{ textShadow: generateTextShadow(0.1) }}
              >
                {isFutureDate
                  ? t("luckyDraw.history.checkBackLater")
                  : t("luckyDraw.history.useCalendarToBrowse")}
              </p>
            </div>
            <DefaultButton
              onClick={() => navigate({ to: "/lucky-draw" })}
              className="mt-4 px-4 py-2"
            >
              {t("luckyDraw.goToLuckyDraw")}
            </DefaultButton>
          </div>
        </div>
      ) : luckyDraw ? (
        <>
          {/* ── Hero Section All ── */}
          <div
            className="relative shrink-0 flex flex-col items-center pb-4"
            style={{
              backgroundImage: `url(${
                hasWinners
                  ? "/assets/ld-end-header_hh4uyZ.png"
                  : "/assets/ld-live-header_uu2Zhy.png"
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              imageRendering: "pixelated",
            }}
          >
            {hasWinners && (
              <canvas
                ref={confettiCanvasRef}
                className="absolute inset-0 w-full h-full pointer-events-none z-10"
              />
            )}
            {/* ── Header ── */}
            <div className="w-full flex justify-end between items-center z-50">
              <div className="pe-2 pt-2 flex flex-col items-end gap-1">
                <DatePickerCalendar
                  value={getDateFromEpoch(selectedEpoch) ?? new Date()}
                  onChange={setSearchDate}
                />
                {particiPantCount != null && particiPantCount > 0 && (
                  <div className="w-[55%] max-w-full brown-container flex items-center justify-center gap-2 py-1">
                    <UserAvatarIcon className="h-3 mb-px w-auto" />
                    <span className="text-xs text-center py-px leading-none">
                      {particiPantCount}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {/* ── Hero section ── */}
            <div
              className="-mt-6 w-fit flex flex-col items-center text-center px-8 py-3"
              style={{
                backgroundImage: `url(${isDistributed ? "/assets/lucky-draw-distributed-screen.png" : "/assets/lucky-draw-live-screen.png"})`,
                backgroundSize: "contain",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            >
              <h2 className="text-xl font-bold text-white uppercase">
                {t("luckyDraw.title")}
              </h2>
              <div className="text-center">
                <ShinyText
                  text={`$${luckyDraw.totalPrizeInUsd}`}
                  className="text-6xl"
                />
                <p
                  className="-mt-1 text-sm uppercase tracking-[0.15em] text-white/90 font-semibold"
                  style={{ textShadow: generateTextShadow(0.35) }}
                >
                  {isDistributed
                    ? t("luckyDraw.history.prizepoolWinners")
                    : t("luckyDraw.history.prizepool")}
                </p>
              </div>
            </div>

            {/* ── Prize chips ── */}
            {sortedPrizes.length > 0 && (
              <div className="mt-2 flex items-center justify-center gap-4 px-4">
                {sortedPrizes.map((prize) => (
                  <div
                    key={prize.prizeOrder}
                    className={cn(
                      "flex flex-col items-center gap-1 bg-black/40 border-2 px-2 py-1",
                      hasWinners
                        ? "border-[#f2c969]/50"
                        : "border-[#BFBEC3]/50",
                    )}
                  >
                    <span
                      className={cn(
                        "text-[12px] font-bold text-nowrap uppercase",
                        hasWinners ? "text-[#f9e0a6]/80" : "text-[#BFBEC3]/80",
                      )}
                    >
                      {t("luckyDraw.history.prizeNumber", {
                        number: prize.prizeOrder,
                      })}
                    </span>
                    <img
                      src="https://cdn.navalabs.io/icons/usdc-icon_24GLmN.png"
                      alt="USDC"
                      className="w-8 h-8 -mt-1"
                      style={{ imageRendering: "pixelated" }}
                    />
                    <span
                      className="text-lg font-semibold text-white -mt-6"
                      style={{ textShadow: generateTextShadow(0.5) }}
                    >
                      {prize.amountUsdc != null
                        ? `$${prize.amountUsdc}`
                        : prize.prizePct != null
                          ? `${prize.prizePct}%`
                          : `#${prize.prizeOrder}`}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex-1 flex flex-col items-center overflow-y-auto">
            {/* ── Live countdown ── */}
            {isLive && luckyDraw.endDate > 0 ? (
              <div className="mt-2 shrink-0 flex flex-col items-center gap-2">
                <span className="text-shadow-black">
                  {t("luckyDraw.history.winnersAnnouncedIn")}
                </span>
                <div className="flex items-center justify-center gap-2 black-container h-8 px-2 opacity-75">
                  <img
                    src="https://cdn.navalabs.io/icons/timer_rqnxy6.png"
                    className="w-3.5"
                    alt="timer"
                  />
                  <Countdown
                    startTimestamp={luckyDraw.endDate}
                    format="compact"
                    doneText="00:00:00"
                    className="text-xs font-medium mt-0.5 text-white/90"
                  />
                </div>
              </div>
            ) : (
              <div className="mt-4 shrink-0 flex flex-col items-center gap-2">
                <span className="text-shadow-black text-xl">
                  {t("luckyDraw.history.winnersAnnounced")}
                </span>
              </div>
            )}

            {/* ── Prize / Winners carousel ── */}
            {sortedPrizes.length > 0 && (
              <div className="w-full flex-1 flex items-center justify-center">
                <HistoryPrizeCarousel
                  prizes={sortedPrizes}
                  hasWinners={hasWinners}
                />
              </div>
            )}

            {/* ── View My Ticket button ── */}
            <div className="mt-auto shrink-0 pb-6 w-full flex justify-center">
              <Sheet>
                <SheetTrigger asChild>
                  <DefaultButton className="px-6 py-2 text-sm uppercase tracking-[0.15em]">
                    {isDistributed
                      ? t("luckyDraw.viewWinningTicket")
                      : t("luckyDraw.history.viewMyTicket")}
                  </DefaultButton>
                </SheetTrigger>
                <SheetContent side="bottom">
                  <SheetHeader>
                    <SheetTitle className="uppercase text-center">
                      {t("luckyDraw.yourLuckyTickets")}
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-2 px-4 pb-4 max-h-80 overflow-y-auto">
                    {orderedEntries.length === 0 ? (
                      <p className="text-white/40 text-sm text-center py-8">
                        {t("luckyDraw.noTicketsEnteredForDraw")}
                      </p>
                    ) : (
                      orderedEntries.map((entry, index) => {
                        const ticketOrder =
                          entryOrderMap.get(entry.id) ?? index + 1;
                        return (
                          <div
                            key={entry.id}
                            className="flex items-center justify-between rounded bg-white/5 px-3 py-2"
                          >
                            <span className="text-white/70 text-xs uppercase">
                              {t("luckyDraw.luckyTicketNumber", {
                                order: ticketOrder,
                              })}
                            </span>
                            <span
                              className={cn(
                                "font-mono text-sm font-semibold tracking-wider",
                                entry.isWinner
                                  ? "text-green-300"
                                  : "text-white",
                              )}
                            >
                              #{entry.ticketCode}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="px-4 pb-4">
                    <SheetClose asChild>
                      <DefaultButton className="w-full">
                        {t("luckyDraw.close")}
                      </DefaultButton>
                    </SheetClose>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ─── Prize / Winners carousel (with avatar + scale-up center) ──────────

type Prize = LuckyDrawDetailByDateResponse["prizes"][number];

function HistoryPrizeCarousel({
  prizes,
  hasWinners,
}: {
  prizes: Prize[];
  hasWinners: boolean;
}) {
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);
  const hasCenteredRef = useRef(false);
  const isSingle = prizes.length === 1;

  const loopedPrizes = useMemo(() => {
    if (prizes.length === 0) return [];
    if (isSingle) return prizes;
    const repeatCount = 3;
    return Array.from(
      { length: prizes.length * repeatCount },
      (_, i) => prizes[i % prizes.length],
    );
  }, [prizes, isSingle]);

  const activeDisplayIndex =
    prizes.length > 0 ? activeIndex % prizes.length : 0;

  useEffect(() => {
    hasCenteredRef.current = false;
  }, [prizes.length]);

  useEffect(() => {
    if (!api) return;

    const handleSelect = () => setActiveIndex(api.selectedScrollSnap());

    const centerIfNeeded = () => {
      if (isSingle || prizes.length === 0 || hasCenteredRef.current) return;
      hasCenteredRef.current = true;
      const centerIndex = prizes.length;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          api.scrollTo(centerIndex, false);
        });
      });
    };

    handleSelect();
    centerIfNeeded();
    api.on("select", handleSelect);
    api.on("reInit", handleSelect);
    api.on("reInit", centerIfNeeded);

    return () => {
      api.off("select", handleSelect);
      api.off("reInit", handleSelect);
      api.off("reInit", centerIfNeeded);
    };
  }, [api, isSingle, prizes.length]);

  return (
    <div className="w-full uppercase tracking-[0.12em]">
      <Carousel
        setApi={setApi}
        opts={{ align: "center", loop: !isSingle, skipSnaps: true }}
        className="w-full"
      >
        <CarouselContent
          className={cn("-ml-3 py-4", isSingle && "ml-0 justify-center")}
        >
          {loopedPrizes.map((prize, index) => {
            const displayIndex = prizes.length ? index % prizes.length : index;
            const winner = prize.winner;
            const isActive = index === activeIndex;

            return (
              <CarouselItem
                key={`${index}-${prize.prizeOrder}`}
                className={cn(
                  "sm:max-w-56 pl-3 transition-all duration-300 hover:cursor-grab active:cursor-grabbing",
                  isSingle ? "basis-full pl-0" : "basis-[55%] sm:basis-[70%]",
                  isActive ? "scale-105 opacity-100" : "scale-85 opacity-40",
                  "py-0.5",
                )}
              >
                <Panel
                  className={cn(
                    "relative overflow-hidden",
                    !hasWinners && "border-[#BFBEC3]/80 text-[#BFBEC3]",
                  )}
                  style={{
                    animation: `lucky-winner-card-in 420ms ease-out ${index * 80}ms both`,
                  }}
                >
                  <div
                    className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 30% 20%, #f2c969 0%, transparent 50%), radial-gradient(circle at 70% 80%, #8b5cf6 0%, transparent 50%)",
                    }}
                  />

                  <div className="relative z-10 h-44 sm:h-52 flex flex-col items-center gap-3">
                    <div className="w-full flex flex-col items-center gap-1.5 sm:gap-2">
                      <span
                        className={cn(
                          "text-[10px]",
                          hasWinners
                            ? "text-[#f9e0a6]/60"
                            : "text-[#BFBEC3]/60",
                        )}
                      >
                        {prizes.length > 1
                          ? t("luckyDraw.winnerWithIndex", {
                              index: displayIndex + 1,
                            })
                          : t("luckyDraw.winnerLabel")}
                      </span>
                      {winner ? (
                        <>
                          {winner.ticketCode && (
                            <span className="text-[#f9e0a6]/80 font-mono font-bold text-xs text-center uppercase -mt-1 leading-5">
                              {t("luckyDraw.luckyTicketNumber", {
                                order: winner.ticketCode,
                              })}
                            </span>
                          )}
                          <div className="flex-1 flex flex-col items-center justify-center">
                            <img
                              src="https://cdn.navalabs.io/icons/usdc-icon_24GLmN.png"
                              alt="USDC"
                              className="w-14 h-14"
                              style={{ imageRendering: "pixelated" }}
                            />
                            <span
                              className="text-2xl font-bold text-[#f9e0a6] -mt-6 sm:-mt-4"
                              style={{ textShadow: generateTextShadow(0.5) }}
                            >
                              {prize.amountUsdc != null
                                ? `$${prize.amountUsdc}`
                                : prize.prizePct != null
                                  ? `${prize.prizePct}%`
                                  : `#${prize.prizeOrder}`}
                            </span>
                          </div>
                          <div className="w-full flex items-center justify-center gap-1">
                            <AvatarBorder
                              src={winner.profilePicture || ""}
                              decoration={winner.activeFrameUrl || ""}
                              size={40}
                            />
                            <UserName
                              username={winner.username}
                              className="text-[#f9e0a6] font-semibold normal-case text-sm truncate"
                              iconSize="w-3 h-3"
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-[#BFBEC3]/50 text-[0.7rem] uppercase tracking-wider">
                          {t("luckyDraw.rewardLabel", {
                            reward:
                              prize.prizePct != null
                                ? `${prize.prizePct}% of pool`
                                : `Prize #${prize.prizeOrder}`,
                          })}
                        </span>
                      )}
                    </div>

                    {!winner && (
                      <div className="flex-1 flex items-center justify-center">
                        <span
                          className="text-6xl font-bold text-[#BFBEC3]/30"
                          style={{
                            textShadow: generateTextShadow(0.5, "#BFBEC3"),
                          }}
                        >
                          ?
                        </span>
                      </div>
                    )}
                  </div>
                </Panel>
              </CarouselItem>
            );
          })}
        </CarouselContent>
      </Carousel>

      {prizes.length > 1 && (
        <div className="hidden sm:flex items-center justify-center gap-1.5 mt-2">
          {prizes.map((_, index) => (
            <button
              key={index}
              className={cn(
                "rounded-full transition-all duration-300",
                index === activeDisplayIndex
                  ? cn(
                      "w-4 h-1.5",
                      hasWinners ? "bg-[#f2c969]/80" : "bg-[#BFBEC3]/80",
                    )
                  : cn(
                      "w-1.5 h-1.5",
                      hasWinners
                        ? "bg-[#f2c969]/30 hover:bg-[#f2c969]/50"
                        : "bg-[#BFBEC3]/30 hover:bg-[#BFBEC3]/50",
                    ),
              )}
              onClick={() => api?.scrollTo(index + prizes.length)}
              aria-label={t("luckyDraw.history.prizeRank", {
                rank: index + 1,
              })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
